//src\pages\Commissions.jsx

import React from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CommissionsTable from '../components/CommissionsTable.jsx';
import ExportCommissions from '../components/ExportCommissions.jsx';
import MonthlyCommissionsTable from '../components/MonthlyCommissionsTable.jsx';
import MonthlyNetSalesTable from '../components/MonthlyNetSalesTable.jsx';
import MonthSelector from '../components/MonthSelector.jsx';
import QuotaBreakdownTable from '../components/QuotaBreakdownTable.jsx';
import RepSelector from '../components/RepSelector.jsx';
import YearOverYearTable from '../components/YearOverYearTable.jsx';
import YearSelector from '../components/YearSelector.jsx';

const IGNORE_REPS = ['SPLIT COMMISSIONS'];

function Commissions() {
  const [selectedMonth, setSelectedMonth] = React.useState();
  const [selectedRep, setSelectedRep] = React.useState('');
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());

  const selectionKey = JSON.stringify([selectedRep, selectedYear, selectedMonth]);
  const [readySelection, setReadySelection] = React.useState(null);
  const handleInitialLoad = React.useCallback(() => {
    setReadySelection(selectionKey);
  }, [selectionKey]);
  const detailReady = readySelection === selectionKey;

  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  const isContractAccounts = React.useMemo(
    () => selectedRep.includes('contract-accounts'),
    [selectedRep]
  );

  const isAllReps = React.useMemo(() => selectedRep === 'all-reps', [selectedRep]);

  const showQuotaBreakdown = React.useMemo(
    () => selectedRep && !isContractAccounts && !isAllReps,
    [selectedRep, isContractAccounts, isAllReps]
  );

  const showMonthlyCommissions = React.useMemo(
    () => selectedRep && !isContractAccounts && !isAllReps,
    [selectedRep, isContractAccounts, isAllReps]
  );

  const showMonthlyNetSales = React.useMemo(
    () => selectedRep && !isContractAccounts,
    [selectedRep, isContractAccounts]
  );

  const ownsATerritory = React.useMemo(
    () => selectedRep && !isAllReps && !isContractAccounts && selectedRep !== 'Casey Johnson',
    [selectedRep, isAllReps, isContractAccounts]
  );

  const showSummarySection =
    showQuotaBreakdown ||
    showMonthlyCommissions ||
    showMonthlyNetSales ||
    (ownsATerritory && selectedYear === currentYear);

  return (
    <Box sx={{ mx: '0.25rem', mb: '0.25rem', mt: '1rem' }}>
      <Stack direction="row" spacing={2}>
        <Typography variant="h4" data-cy="commissions-title">
          Commissions
        </Typography>
        <RepSelector ignoreReps={IGNORE_REPS} onChange={setSelectedRep} selectedRep={selectedRep} />
        <YearSelector selectedYear={selectedYear} onChange={setSelectedYear} />
        <MonthSelector selectedMonth={selectedMonth} onChange={setSelectedMonth} />
        <ExportCommissions
          selectedMonth={selectedMonth}
          selectedRep={selectedRep}
          selectedYear={selectedYear}
        />
      </Stack>
      {showSummarySection && (
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          {showQuotaBreakdown && (
            <Grid size={6}>
              <QuotaBreakdownTable
                enabled={detailReady}
                selectedRep={selectedRep}
                selectedYear={selectedYear}
              />
            </Grid>
          )}
          {ownsATerritory && selectedYear === currentYear && (
            <Grid size={6}>
              <YearOverYearTable
                enabled={detailReady}
                selectedRep={selectedRep}
                selectedYear={selectedYear}
              />
            </Grid>
          )}
          {showMonthlyCommissions && (
            <Grid size={6}>
              <MonthlyCommissionsTable
                enabled={detailReady}
                selectedRep={selectedRep}
                selectedYear={selectedYear}
              />
            </Grid>
          )}
          {showMonthlyNetSales && (
            <Grid size={6}>
              <MonthlyNetSalesTable
                enabled={detailReady}
                selectedRep={selectedRep}
                selectedYear={selectedYear}
              />
            </Grid>
          )}
        </Grid>
      )}
      {selectedRep ? (
        <Box sx={{ mt: 3 }}>
          <CommissionsTable
            key={selectionKey}
            onInitialLoad={handleInitialLoad}
            selectedRep={selectedRep}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
          />
        </Box>
      ) : (
        <Typography
          variant="h6"
          align="center"
          sx={{ mt: 4, color: 'text.secondary' }}
          data-cy="select-rep-message"
        >
          Please select a rep above
        </Typography>
      )}
    </Box>
  );
}

export default React.memo(Commissions);
